import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CargoProblemListQuery,
  CargoProblemMaterialsQuery,
  CargoProblemMessageCreateInput,
  CargoProblemQueue,
  CargoProblemTransitionInput,
  CargoProblemWorkspaceTransitionInput,
  CreateCargoProblemInput,
  CreateCargoProblemWorkspaceInput,
  PatchCargoProblemInput,
} from '@magimp/shared';
import {
  CARGO_PROBLEMS_QUERY_ROOT,
  createCargoProblem,
  createCargoProblemWorkspace,
  deleteCargoProblem,
  deleteCargoProblemMaterial,
  getCargoProblemAssigneeCandidates,
  getCargoProblem,
  getCargoProblemActivity,
  getCargoProblemAttention,
  getCargoProblemFilterOptions,
  getCargoProblemMaterials,
  getCargoProblemSummary,
  getCargoProblemWorkspaceCapabilities,
  getCargoProblemWorkspaceMessages,
  getProblemAssigneeCandidates,
  listCargoProblems,
  markCargoProblemRead,
  patchCargoProblem,
  postCargoProblemWorkspaceMessage,
  reassignCargoProblem,
  replaceCargoProblemWorkspace,
  searchCargoProblemCandidates,
  transitionCargoProblem,
  transitionCargoProblemWorkspace,
} from '../../api/cargo-problems.js';

export const cargoProblemsKeys = {
  all: CARGO_PROBLEMS_QUERY_ROOT,
  list: (query: CargoProblemListQuery) => [...CARGO_PROBLEMS_QUERY_ROOT, 'list', query] as const,
  summary: (query: CargoProblemListQuery) =>
    [...CARGO_PROBLEMS_QUERY_ROOT, 'summary', query] as const,
  options: (query: CargoProblemListQuery) =>
    [...CARGO_PROBLEMS_QUERY_ROOT, 'options', query] as const,
  attention: () => [...CARGO_PROBLEMS_QUERY_ROOT, 'workspace-attention'] as const,
  detail: (id: string) => [...CARGO_PROBLEMS_QUERY_ROOT, 'detail', id] as const,
  workspaceMessages: (id: string) =>
    [...CARGO_PROBLEMS_QUERY_ROOT, 'workspace-messages', id] as const,
  materials: (id: string, query: CargoProblemMaterialsQuery) =>
    [...CARGO_PROBLEMS_QUERY_ROOT, 'workspace-materials', id, query] as const,
  activity: (id: string) => [...CARGO_PROBLEMS_QUERY_ROOT, 'activity', id] as const,
  candidates: (q: string) => [...CARGO_PROBLEMS_QUERY_ROOT, 'candidates', q] as const,
  assigneeCandidates: (queue: CargoProblemQueue) =>
    [...CARGO_PROBLEMS_QUERY_ROOT, 'assignee-candidates', queue] as const,
  problemAssigneeCandidates: (id: string) =>
    [...CARGO_PROBLEMS_QUERY_ROOT, 'problem-assignee-candidates', id] as const,
  workspaceCapabilities: (id: string) =>
    [...CARGO_PROBLEMS_QUERY_ROOT, 'workspace-capabilities', id] as const,
};

export function useCargoProblemsList(query: CargoProblemListQuery) {
  return useQuery({
    queryKey: cargoProblemsKeys.list(query),
    queryFn: ({ signal }) => listCargoProblems(query, signal),
  });
}
export function useCargoProblemSummary(query: CargoProblemListQuery) {
  return useQuery({
    queryKey: cargoProblemsKeys.summary(query),
    queryFn: ({ signal }) => getCargoProblemSummary(query, signal),
  });
}
export function useCargoProblemFilterOptions(query: CargoProblemListQuery) {
  return useQuery({
    queryKey: cargoProblemsKeys.options(query),
    queryFn: ({ signal }) => getCargoProblemFilterOptions(query, signal),
  });
}
export function useCargoProblemAttention(enabled = true) {
  return useQuery({
    queryKey: cargoProblemsKeys.attention(),
    queryFn: ({ signal }) => getCargoProblemAttention(signal),
    enabled,
  });
}
export function useCargoProblemDetail(id: string) {
  return useQuery({
    queryKey: cargoProblemsKeys.detail(id),
    queryFn: ({ signal }) => getCargoProblem(id, signal),
  });
}
/**
 * @aiInvariant the cursor walks BACKWARDS into history, so `fetchNextPage`
 *   loads older messages. New rows never arrive through the cursor — they
 *   arrive when the newest page is invalidated (realtime, focus, own send).
 */
export function useCargoProblemWorkspaceStream(id: string) {
  return useInfiniteQuery({
    queryKey: cargoProblemsKeys.workspaceMessages(id),
    queryFn: ({ pageParam, signal }) =>
      getCargoProblemWorkspaceMessages(id, pageParam ? { cursor: pageParam } : {}, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
}
export function useCargoProblemMaterials(id: string, query: CargoProblemMaterialsQuery) {
  return useQuery({
    queryKey: cargoProblemsKeys.materials(id, query),
    queryFn: ({ signal }) => getCargoProblemMaterials(id, query, signal),
  });
}
export function useCargoProblemActivity(id: string) {
  return useQuery({
    queryKey: cargoProblemsKeys.activity(id),
    queryFn: ({ signal }) => getCargoProblemActivity(id, signal),
  });
}
export function useCargoProblemCandidates(q: string, active: boolean) {
  const normalized = q.trim();
  return useQuery({
    queryKey: cargoProblemsKeys.candidates(normalized),
    queryFn: ({ signal }) => searchCargoProblemCandidates(normalized, signal),
    enabled: active && (normalized.length === 0 || normalized.length >= 2),
  });
}
export function useCargoProblemAssigneeCandidates(queue: CargoProblemQueue, enabled = true) {
  return useQuery({
    queryKey: cargoProblemsKeys.assigneeCandidates(queue),
    queryFn: ({ signal }) => getCargoProblemAssigneeCandidates(queue, signal),
    enabled,
  });
}
export function useProblemAssigneeCandidates(id: string, enabled = true) {
  return useQuery({
    queryKey: cargoProblemsKeys.problemAssigneeCandidates(id),
    queryFn: ({ signal }) => getProblemAssigneeCandidates(id, signal),
    enabled,
  });
}
export function useCargoProblemWorkspaceCapabilities(id: string) {
  return useQuery({
    queryKey: cargoProblemsKeys.workspaceCapabilities(id),
    queryFn: ({ signal }) => getCargoProblemWorkspaceCapabilities(id, signal),
  });
}

function useInvalidateCargoProblems() {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: CARGO_PROBLEMS_QUERY_ROOT });
}
export function useCreateCargoProblem() {
  const invalidate = useInvalidateCargoProblems();
  return useMutation({
    mutationFn: (input: CreateCargoProblemInput) => createCargoProblem(input),
    onSuccess: invalidate,
  });
}
export function useCreateCargoProblemWorkspace() {
  const invalidate = useInvalidateCargoProblems();
  return useMutation({
    mutationFn: (input: CreateCargoProblemWorkspaceInput) => createCargoProblemWorkspace(input),
    onSuccess: invalidate,
  });
}
export function useReplaceCargoProblemWorkspace() {
  const invalidate = useInvalidateCargoProblems();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CreateCargoProblemWorkspaceInput }) =>
      replaceCargoProblemWorkspace(id, input),
    onSuccess: invalidate,
  });
}
export function usePatchCargoProblem(id: string) {
  const invalidate = useInvalidateCargoProblems();
  return useMutation({
    mutationFn: (input: PatchCargoProblemInput) => patchCargoProblem(id, input),
    onSuccess: invalidate,
  });
}
export function useDeleteCargoProblem(id: string) {
  const invalidate = useInvalidateCargoProblems();
  return useMutation({ mutationFn: () => deleteCargoProblem(id), onSuccess: invalidate });
}
export function useTransitionCargoProblem(id: string) {
  const invalidate = useInvalidateCargoProblems();
  return useMutation({
    mutationFn: (input: CargoProblemTransitionInput) => transitionCargoProblem(id, input),
    onSuccess: invalidate,
  });
}
export function useTransitionCargoProblemWorkspace() {
  const invalidate = useInvalidateCargoProblems();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CargoProblemWorkspaceTransitionInput }) =>
      transitionCargoProblemWorkspace(id, input),
    onSuccess: invalidate,
  });
}
export function useReassignCargoProblem(id: string) {
  const invalidate = useInvalidateCargoProblems();
  return useMutation({
    mutationFn: (assigneeId: string) => reassignCargoProblem(id, assigneeId),
    onSuccess: invalidate,
  });
}
export function usePostCargoProblemMessage(id: string) {
  const invalidate = useInvalidateCargoProblems();
  return useMutation({
    mutationFn: (input: CargoProblemMessageCreateInput) =>
      postCargoProblemWorkspaceMessage(id, input),
    onSuccess: invalidate,
  });
}
export function useDeleteCargoProblemMaterial(id: string) {
  const invalidate = useInvalidateCargoProblems();
  return useMutation({
    mutationFn: ({ fileId, reason }: { fileId: string; reason: string }) =>
      deleteCargoProblemMaterial(id, fileId, reason),
    onSuccess: invalidate,
  });
}
/**
 * WHY: the read pointer moves as the reader scrolls, so it must not invalidate
 * the conversation — a re-read on every advance would fight the scroll. Only
 * the attention surface (badge + list markers) is refreshed.
 */
export function useMarkCargoProblemRead(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => markCargoProblemRead(id, messageId),
    onSuccess: () => client.invalidateQueries({ queryKey: cargoProblemsKeys.attention() }),
  });
}
